import { Stack } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { handleUnauthorizedResponse } from '../../../api/http';
import PremiumReportingApi from '../../../api/PremiumReportingApi';
import { AuthData } from '../../../context/auth';
import { PolicyData, withPolicyContext } from '../../../context/policy';
import { dayjs } from '../../../date-util';
import {
	PremiumReportCreateRequest,
	PremiumReportResponse,
} from '../../../types/premiumReports';
import {
	canUsePaymentPlugin,
	getPolicyStatus,
	policyIsWithdrawn,
} from '../../../util/util';
import DropdownButton from '../../DropdownButton';
import { getNewPremiumReportEndDate } from '../../premiumReports/util';
import { getCurrentGraceLapse } from '../../search/util';
import { errorToast } from '../../Toast';
import CancellationTypesModalTrigger from '../CancellationTypesModalTrigger';
import ChangePaymentPlanTrigger from '../ChangePaymentPlanTrigger';
import EndorsementCreateTrigger from '../Endorsements/EndorsementCreateTrigger';
import RenewalCreateTrigger from '../Renewals/RenewalCreateTrigger';

interface Props {
	policyData: PolicyData;
	authData: AuthData;
}

const PolicyActions = (props: Props) => {
	const navigate = useNavigate();
	const { policyData, authData } = props;
	const { productConfig, policy, graceLapse, setLoading } = policyData;
	const { hasPermission } = authData;
	const { policyConfiguration } = productConfig;

	const { data: premiumReportEndDates } = useQuery({
		queryKey: [
			'reportEndDates',
			policy.locator,
			authData.auth.authorizationToken,
		],
		enabled:
			!!policy &&
			!!policy?.premiumReportName &&
			hasPermission('premiumReport.fetch'),
		onError: (error) => {
			handleUnauthorizedResponse(error as AxiosError, authData);
		},
		queryFn: async () => {
			const premiumReportResponse = await PremiumReportingApi.getPremiumReports(
				authData.auth.authorizationToken,
				policy.locator,
			);
			const premiumReports = premiumReportResponse.data;
			const issuedReports =
				premiumReports &&
				premiumReports.filter(
					(report: PremiumReportResponse) => report.state === 'issued',
				);
			const issuedReportEndDates =
				issuedReports &&
				issuedReports.map(
					(report: PremiumReportResponse) => report.endTimestamp,
				);
			return issuedReportEndDates;
		},
		retry: false,
		refetchOnWindowFocus: false,
	});

	if (!graceLapse) {
		return null;
	}

	const state = getPolicyStatus(policy, getCurrentGraceLapse(graceLapse));
	const hiddenActions: any[] = [];

	if (policyIsWithdrawn(policy)) {
		return null;
	}

	const getEndorsementActions = () => {
		if (
			hasPermission('endorsement.create') &&
			hasPermission('endorsement.fetch')
		) {
			const endorsements = policyConfiguration.endorsements;
			endorsements.forEach((e, index) => {
				hiddenActions.push({
					label: null,
					callback: null,
					component: (
						<EndorsementCreateTrigger
							key={`endorsement-${e.name}`}
							endorsement={e}
							premiumReportEndDates={premiumReportEndDates || []}
						/>
					),
				});
			});
		}
	};

	const getRenewalActions = () => {
		const renewals = policyConfiguration.renewals;
		if (
			renewals &&
			hasPermission('renewal.create') &&
			hasPermission('renewal.fetch')
		) {
			hiddenActions.push({
				label: null,
				callback: null,
				component: <RenewalCreateTrigger />,
			});
		}
	};

	const getPremiumReportingAction = () => {
		const premiumReporting = productConfig.premiumReportingConfiguration;
		if (
			premiumReporting &&
			hasPermission('premiumReport.create') &&
			hasPermission('premiumReport.fetch')
		) {
			hiddenActions.push({
				label: 'Create Premium Report',
				callback: createPremiumReport,
			});
		}
	};

	const getChangePaymentPlanAction = () => {
		const hasPaymentPlanPluginEnabled = canUsePaymentPlugin(
			productConfig.policyConfiguration.plugins,
		);
		if (
			hasPaymentPlanPluginEnabled &&
			hasPermission('policy.paymentSchedule')
		) {
			hiddenActions.push({
				label: null,
				callback: null,
				component: <ChangePaymentPlanTrigger />,
			});
		}
	};

	const createPremiumReport = async () => {
		// get previous premium reports, if any
		try {
			setLoading(true);
			const currPremiumReports = await PremiumReportingApi.getPremiumReports(
				authData.auth.authorizationToken,
				policy.locator,
			);
			const todaysDate = new Date();
			todaysDate.setHours(0, 0, 0, 0);
			const todaysDateWithTZ = dayjs(todaysDate).tz(authData.auth.timezone);
			const endDate = getNewPremiumReportEndDate(
				currPremiumReports.data,
				policy,
			);
			const premiumReportCreateReq: PremiumReportCreateRequest = {
				endTimestamp: endDate,
				invoiceDueTimestamp: todaysDateWithTZ.valueOf().toString(),
				fieldGroups: [],
				fieldValues: {},
			};
			const premiumReportResponse =
				await PremiumReportingApi.createPremiumReport(
					authData.auth.authorizationToken,
					policy.locator,
					premiumReportCreateReq,
				);
			setLoading(false);
			navigate(
				`/policy/${policyData.policy.displayId}/premiumReports/${premiumReportResponse.data.locator}/details`,
			);
		} catch (error) {
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
			errorToast('Unable to create premium report');
			setLoading(false);
		}
	};

	getPremiumReportingAction();
	getEndorsementActions();

	if (state !== 'Cancelled') {
		getRenewalActions();
	}

	if (
		hasPermission('cancellation.create') &&
		hasPermission('cancellation.fetch') &&
		productConfig &&
		productConfig.policyConfiguration.cancellations &&
		productConfig.policyConfiguration.cancellations.reasons &&
		policy.originalContractStartTimestamp !==
			policy.effectiveContractEndTimestamp // policy not already a flat cancel
	) {
		hiddenActions.push({
			label: 'Cancel Policy',
			callback: null,
			component: <CancellationTypesModalTrigger key='cancellationTrigger' />,
		});
	}

	getChangePaymentPlanAction();

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<Stack direction='row' spacing={1}>
				<DropdownButton actions={hiddenActions} />
			</Stack>
		</LocalizationProvider>
	);
};

export default withPolicyContext<Props>(PolicyActions);
